import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Plane, Clock, Heart, Search, Columns3, Users, ArrowLeft, Eye } from "lucide-react";
import Seo from "../components/Seo";
import ShareButton from "../components/ShareButton";
import BookButtons from "../components/BookButtons";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { airlineLogo, fmtPoints, fmtDate, CABINS, TRANSFER_PARTNERS, estimateCashFare, centsPerPoint } from "../data/mock";
import { useWishlist } from "../context/WishlistContext";
import { useToast } from "../hooks/use-toast";
import { log } from "../lib/log";

const cabinLabel = (k) => (CABINS.find((c) => c.key === k) || {}).label || k;

export default function Deal() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const wishlist = useWishlist();
  const { toast } = useToast();

  useEffect(() => {
    api.getDeal(id).then(setData).catch((err) => { log.warn("Shared deal not found", id, err?.response?.status); setError(true); });
  }, [id]);

  if (error) {
    return (
      <main className="min-h-[70vh] bg-slate-50">
        <div className="mx-auto max-w-3xl px-5 py-24 text-center" data-testid="deal-not-found">
          <Plane className="mx-auto h-12 w-12 text-slate-300" />
          <h1 className="mt-4 text-2xl font-bold text-slate-900">This deal link has expired</h1>
          <p className="mt-2 text-slate-600">We couldn’t find that shared flight. Run a fresh search to find similar availability.</p>
          <Link to="/search" className="mt-6 inline-block"><Button className="gap-2 bg-blue-600 hover:bg-blue-700"><Search className="h-4 w-4" /> Search flights</Button></Link>
        </div>
      </main>
    );
  }
  if (!data) {
    return <main className="min-h-[70vh] bg-slate-50"><div className="mx-auto max-w-3xl px-5 py-24 text-center text-slate-500" data-testid="deal-loading">Loading deal…</div></main>;
  }

  const d = data.deal;
  const cash = estimateCashFare(d.distance, d.cabin);
  const cpp = centsPerPoint(cash, d.points, d.taxes.amount);
  const saved = wishlist?.isSaved(d.id);
  const title = `${d.origin.code} → ${d.destination.code} in ${cabinLabel(d.cabin)} for ${fmtPoints(d.points)} pts | FindMyFlight`;
  const description = `${d.airline} (${d.program}) award seat from ${d.origin.city} to ${d.destination.city} on ${fmtDate(d.date)} — ${fmtPoints(d.points)} points + $${d.taxes.amount} taxes.`;
  const jsonLd = { "@context": "https://schema.org", "@type": "Offer", name: title, description, price: d.taxes.amount, priceCurrency: "USD" };

  const onSave = async () => {
    const res = await wishlist.toggle(d);
    if (res?.needAuth) toast({ title: "Sign in to save deals", description: "Create a free account to build your wishlist." });
    else if (res?.added) toast({ title: "Saved to wishlist" });
  };

  return (
    <main className="min-h-[70vh] bg-slate-50">
      <Seo title={title} description={description} path={`/deal/${id}`} jsonLd={jsonLd} />
      <div className="mx-auto max-w-3xl px-5 py-10">
        <Link to="/search" className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> Back to search</Link>

        <div data-testid="deal-detail" className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-200/60">
          <div className="bg-blue-600 px-6 py-5 text-white sm:px-8">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-100">Shared award deal · {fmtDate(d.date)}</p>
            <div className="mt-3 flex items-center justify-between gap-4">
              <div>
                <p className="text-4xl font-extrabold sm:text-5xl">{d.origin.code}</p>
                <p className="text-sm text-blue-100">{d.origin.city}</p>
              </div>
              <div className="flex flex-1 flex-col items-center">
                <div className="flex w-full items-center gap-2">
                  <div className="h-px flex-1 bg-blue-300/60" />
                  <Plane className="h-5 w-5 rotate-90" />
                  <div className="h-px flex-1 bg-blue-300/60" />
                </div>
                <span className="mt-1 flex items-center gap-1 text-xs text-blue-100"><Clock className="h-3 w-3" /> {d.duration} · {new Intl.NumberFormat().format(d.distance)} mi</span>
              </div>
              <div className="text-right">
                <p className="text-4xl font-extrabold sm:text-5xl">{d.destination.code}</p>
                <p className="text-sm text-blue-100">{d.destination.city}</p>
              </div>
            </div>
          </div>

          <div className="grid gap-6 px-6 py-6 sm:grid-cols-2 sm:px-8">
            <div className="flex items-center gap-3">
              <img src={airlineLogo(d.airlineCode)} alt={d.airline} className="h-12 w-12 rounded-lg border border-slate-100 object-contain p-1" onError={(e) => { e.target.style.visibility = "hidden"; }} />
              <div>
                <p className="text-lg font-bold text-slate-900">{d.airline}</p>
                <p className="text-sm text-slate-500">{d.program} · <span className="capitalize">{cabinLabel(d.cabin)}</span></p>
              </div>
            </div>
            <div className="sm:text-right">
              <p className="text-3xl font-extrabold text-blue-600" data-testid="deal-points">{fmtPoints(d.points)} <span className="text-sm font-semibold text-slate-500">pts</span></p>
              <p className="text-sm text-slate-500">+ ${d.taxes.amount} {d.taxes.currency} taxes & fees</p>
            </div>
          </div>

          <div className="grid grid-cols-3 divide-x divide-slate-100 border-y border-slate-100 bg-slate-50 text-center">
            <div className="px-3 py-4"><p className="text-xs uppercase tracking-wide text-slate-500">Est. cash fare</p><p className="mt-1 text-lg font-bold text-slate-900">${new Intl.NumberFormat().format(cash)}</p></div>
            <div className="px-3 py-4"><p className="text-xs uppercase tracking-wide text-slate-500">Value</p><p className="mt-1 text-lg font-bold text-green-700">{cpp}¢ / pt</p></div>
            <div className="px-3 py-4"><p className="text-xs uppercase tracking-wide text-slate-500">Seats</p><p className="mt-1 flex items-center justify-center gap-1 text-lg font-bold text-slate-900"><Users className="h-4 w-4 text-slate-400" /> {d.seats || "—"}</p></div>
          </div>

          {d.partners?.length > 0 && (
            <div className="px-6 py-5 sm:px-8">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Transfer points from</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {d.partners.map((p) => TRANSFER_PARTNERS[p] && (
                  <span key={p} className="rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ backgroundColor: TRANSFER_PARTNERS[p].color }}>{TRANSFER_PARTNERS[p].name}</span>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-slate-100 px-6 py-5 sm:px-8">
            <BookButtons deal={d} size="lg" source="deal_page" />
          </div>
          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-6 py-5 sm:px-8">
            <ShareButton deal={d} variant="button" />
            <button onClick={onSave} data-testid="deal-save-button" className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${saved ? "border-red-200 bg-red-50 text-red-500" : "border-slate-200 bg-white text-slate-700 hover:border-red-200 hover:text-red-500"}`}>
              <Heart className="h-4 w-4" fill={saved ? "currentColor" : "none"} /> {saved ? "Saved" : "Save to wishlist"}
            </button>
            <Link to={`/cabin-compare?from=${d.origin.code}&to=${d.destination.code}&date=${d.date}`} data-testid="deal-compare-cabins" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-blue-300 hover:text-blue-600"><Columns3 className="h-4 w-4" /> Compare cabins</Link>
            <Link to={`/search?from=${d.origin.code}&to=${d.destination.code}&date=${d.date}&flex=3`} data-testid="deal-search-route" className="ml-auto inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700"><Search className="h-4 w-4" /> Search this route</Link>
          </div>
        </div>

        <p className="mt-4 flex items-center justify-center gap-1 text-xs text-slate-400"><Eye className="h-3.5 w-3.5" /> Viewed {data.views} time{data.views === 1 ? "" : "s"} · Availability changes fast — verify with the airline before transferring points.</p>
      </div>
    </main>
  );
}
